import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import dayjs from 'dayjs'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import ButtonGroupCustomized from '../ButtonGroupCustomized'
import MonthRangeSelector from '../MonthRangeSelector'

interface PeriodOption {
    value: string
    label: string
}

export interface MetricOption {
    key: string
    label: string
    color: string
}

export interface PeriodSelection {
    startDate: string // YYYY-MM-DD
    endDate: string // YYYY-MM-DD
    viewMode: 'year' | 'quarter' | 'month'
    selectedPeriods: Set<string> // e.g., ['Q1', 'Q2'] or ['1', '2', '3']
    selectedMetrics: Set<string>
}

interface PeriodComparisonFilterProps {
    metrics: MetricOption[]
    onChange: (selection: PeriodSelection) => void
    defaultStartDate?: string
    defaultEndDate?: string
    className?: string
}

// Helper function to generate available periods (moved outside component for reusability)
const generateAvailablePeriods = (
    mode: 'year' | 'quarter' | 'month',
    start: string,
    end: string
): PeriodOption[] => {
    const periods: PeriodOption[] = []
    const startDay = dayjs(start)
    const endDay = dayjs(end)

    if (mode === 'year') {
        const startYear = startDay.year()
        const endYear = endDay.year()
        for (let year = startYear; year <= endYear; year++) {
            periods.push({
                value: String(year),
                label: String(year),
            })
        }
    } else if (mode === 'quarter') {
        periods.push(
            { value: 'quarter_1', label: 'quarter_1' },
            { value: 'quarter_2', label: 'quarter_2' },
            { value: 'quarter_3', label: 'quarter_3' },
            { value: 'quarter_4', label: 'quarter_4' }
        )
    } else if (mode === 'month') {
        const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ]
        monthNames.forEach((month, idx) => {
            periods.push({
                value: String(idx + 1),
                label: month,
            })
        })
    }

    return periods
}

const PeriodComparisonFilter: React.FC<PeriodComparisonFilterProps> = ({
    metrics,
    onChange,
    defaultStartDate = dayjs().startOf('year').format('YYYY-MM-DD'),
    defaultEndDate = dayjs().endOf('year').format('YYYY-MM-DD'),
    className = '',
}) => {
    const { t } = useTranslation()

    const [startDate, setStartDate] = useState(defaultStartDate)
    const [endDate, setEndDate] = useState(defaultEndDate)
    const [viewMode, setViewMode] = useState<'year' | 'quarter' | 'month'>(
        'quarter'
    )
    // Initialize selectedPeriods with all periods of the default viewMode
    const [selectedPeriods, setSelectedPeriods] = useState<Set<string>>(() => {
        const initialPeriods = generateAvailablePeriods(
            'quarter',
            defaultStartDate,
            defaultEndDate
        )
        return new Set(initialPeriods.map((p) => p.value))
    })
    const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(
        new Set(metrics.map((m) => m.key))
    )

    // Generate available periods based on view mode
    const availablePeriods = useMemo(() => {
        return generateAvailablePeriods(viewMode, startDate, endDate)
    }, [startDate, endDate, viewMode])

    // Handle changes and notify parent
    const notifyChange = (updates: Partial<PeriodSelection>) => {
        onChange({
            startDate,
            endDate,
            viewMode,
            selectedPeriods,
            selectedMetrics,
            ...updates,
        })
    }

    const handleStartDateChange = (value: string) => {
        setStartDate(value)
        notifyChange({ startDate: value })
    }

    const handleEndDateChange = (value: string) => {
        setEndDate(value)
        notifyChange({ endDate: value })
    }

    const handleViewModeChange = (mode: 'year' | 'quarter' | 'month') => {
        setViewMode(mode)
        // Auto-select all periods for the new view mode
        const newAvailablePeriods = generateAvailablePeriods(
            mode,
            startDate,
            endDate
        )
        const allPeriods = new Set(newAvailablePeriods.map((p) => p.value))
        setSelectedPeriods(allPeriods)
        notifyChange({ viewMode: mode, selectedPeriods: allPeriods })
    }

    const togglePeriod = (period: string) => {
        const newSelected = new Set(selectedPeriods)
        if (newSelected.has(period)) {
            newSelected.delete(period)
        } else {
            newSelected.add(period)
        }
        setSelectedPeriods(newSelected)
        notifyChange({ selectedPeriods: newSelected })
    }

    const toggleAllPeriods = () => {
        if (selectedPeriods.size === availablePeriods.length) {
            setSelectedPeriods(new Set())
            notifyChange({ selectedPeriods: new Set() })
        } else {
            const allPeriods = new Set(availablePeriods.map((p) => p.value))
            setSelectedPeriods(allPeriods)
            notifyChange({ selectedPeriods: allPeriods })
        }
    }

    const toggleMetric = (key: string) => {
        const newSelected = new Set(selectedMetrics)
        if (newSelected.has(key)) {
            newSelected.delete(key)
        } else {
            newSelected.add(key)
        }
        setSelectedMetrics(newSelected)
        notifyChange({ selectedMetrics: newSelected })
    }

    const toggleAllMetrics = () => {
        if (selectedMetrics.size === metrics.length) {
            setSelectedMetrics(new Set())
            notifyChange({ selectedMetrics: new Set() })
        } else {
            const allMetrics = new Set(metrics.map((m) => m.key))
            setSelectedMetrics(allMetrics)
            notifyChange({ selectedMetrics: allMetrics })
        }
    }
    return (
        <div
            className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}
        >
            <Typogaphy className="font-medium text-primary mb-2">
                {t('period_comparison')}
            </Typogaphy>
            {/* Date Range Selector */}
            <div className="grid xs:grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                <MonthRangeSelector
                    value={{
                        gte: startDate,
                        lte: endDate,
                    }}
                    onChange={(values) => {
                        if (!values.gte || !values.lte) return
                        handleStartDateChange(values.gte)
                        handleEndDateChange(values.lte)
                    }}
                />
                <div className="space-y-4">
                    <ButtonGroupCustomized
                        buttons={[
                            {
                                label: t('year'),
                                value: 'year',
                            },
                            {
                                label: t('quarter'),
                                value: 'quarter',
                            },
                            {
                                label: t('month'),
                                value: 'month',
                            },
                        ]}
                        active={viewMode}
                        onChange={(value) =>
                            handleViewModeChange(
                                value as 'year' | 'quarter' | 'month'
                            )
                        }
                    />
                    <div className="flex flex-wrap gap-4 items-center">
                        {/* Select All Periods */}
                        <div className="flex items-center gap-2 min-w-[120px]">
                            <Checkbox
                                id="select-all-periods"
                                checked={
                                    selectedPeriods.size ===
                                        availablePeriods.length &&
                                    availablePeriods.length > 0
                                }
                                onCheckedChange={toggleAllPeriods}
                            />
                            <Label
                                htmlFor="select-all-periods"
                                className="text-sm cursor-pointer"
                            >
                                {t('select_all')}
                            </Label>
                        </div>
                        {availablePeriods?.map((period) => (
                            <div
                                key={period?.value}
                                className="flex items-center gap-2 min-w-[120px]"
                            >
                                <Checkbox
                                    id={`period-${period.value}`}
                                    checked={selectedPeriods?.has(
                                        period?.value
                                    )}
                                    onCheckedChange={() =>
                                        togglePeriod(period.value)
                                    }
                                />
                                <Label
                                    htmlFor={`period-${period.value}`}
                                    className="text-sm cursor-pointer"
                                >
                                    {t(period?.label)}
                                </Label>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {/* Period Selection */}
            <div className="mb-2"></div>

            {/* Metric Checkboxes */}
            <div className="border-t border-gray-200 pt-4">
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="select-all-metrics"
                            checked={selectedMetrics.size === metrics.length}
                            onCheckedChange={toggleAllMetrics}
                        />
                        <Label
                            htmlFor="select-all-metrics"
                            className="text-sm cursor-pointer"
                        >
                            {t('select_all')}
                        </Label>
                    </div>

                    {metrics.map((metric) => (
                        <div
                            key={metric.key}
                            className="flex items-center gap-2"
                        >
                            <Checkbox
                                id={`metric-${metric.key}`}
                                checked={selectedMetrics.has(metric.key)}
                                onCheckedChange={() => toggleMetric(metric.key)}
                                style={
                                    selectedMetrics.has(metric.key)
                                        ? {
                                              backgroundColor: metric.color,
                                              borderColor: metric.color,
                                          }
                                        : {
                                              borderColor: metric.color,
                                          }
                                }
                            />
                            <Label
                                htmlFor={`metric-${metric.key}`}
                                className="text-sm cursor-pointer"
                                style={{
                                    color: selectedMetrics.has(metric.key)
                                        ? metric.color
                                        : undefined,
                                }}
                            >
                                {t(metric?.label)}
                            </Label>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default PeriodComparisonFilter
